import React, { Fragment, FunctionComponent, useContext, useState } from 'react';
import { UseFormRegister } from 'react-hook-form/dist/types/form';
import { InputHolder, InputHolderItem } from '../../shared/InputHolder';
import { Input } from '../../shared/Input';
import { formItems } from '../RefinanceStepper/formItems';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import { Select } from '../../shared/Select';
import { getDefaultValue, isPercentage, isValidDate, isWholeNumber } from '../../../utils/validations';
import { Text } from '../../shared/Text';
import { Radio } from '../../shared/Radio';
import { convertDateObject, parseDate } from '../../../utils/dates';
import { addDays, addYears, isAfter, isBefore, subYears } from 'date-fns';
import { FormContext, FormItem } from '../../../store/refinance/RefinanceContext';

export const MortgagePart: FunctionComponent<{
	register: UseFormRegister<any>,
	errors: FieldErrors<any>,
	part: number,
	getValues: any,
	setError: any,
	clearErrors: any,
	values?: FormItem  }> = ({register, errors, part, getValues, setError, clearErrors, values}) => {
	const [rvpEndDateError, setRvpEndDateError] = useState('');
	const [mortgageTermStartError, setMortgageTermStartError] = useState('');
	const [showExtraFields, setShowExtraFields] = useState<boolean>(
		(typeof getDefaultValue(values, formItems.step2.mortgagePart.isThePenaltyAmountKnown) === 'boolean')
			? getDefaultValue(values, formItems.step2.mortgagePart.isThePenaltyAmountKnown) === false : false);

	const { form } = useContext(FormContext);

	function getFormName(name: string): string {
		return `${formItems.step2.mortgageParts}.${part}.${name}`
	}

	function hasError(name: string) {
		if (typeof errors[formItems.step2.mortgageParts] !== 'undefined') {
			if (typeof errors[formItems.step2.mortgageParts][part] !== 'undefined') {
				return errors[formItems.step2.mortgageParts][part][name];
			}
		}
	}

	function fireError(errorMsg: string, type: 'MortgageTermStartDate' | 'RvpEndDate') {
		(type === 'MortgageTermStartDate') ? setMortgageTermStartError(errorMsg) : setRvpEndDateError(errorMsg);
		setError(getFormName((type === 'MortgageTermStartDate') ? formItems.step2.mortgagePart.mortgageTermStart : formItems.step2.mortgagePart.interestRateFixedUntil));
		return false;
	}

	/**
	 * Validate mortgageTermStart
	 * @param mortgageTermStart
	 */
	function validateMortgageTermStartDate(mortgageTermStart: string) {
		// check if date is not valid
		if (!isValidDate(mortgageTermStart)) {
			return fireError(`datum is niet correct`, 'MortgageTermStartDate');
		}

		// check if mortgage is already paid off -> 30, 15-05-1981
		const mortgageTermYears = getValues(getFormName(formItems.step2.mortgagePart.mortgageTermYears));
		if (mortgageTermYears) {
			const mortgageTermEndDate = addYears(parseDate(mortgageTermStart), mortgageTermYears);
			if (isBefore(mortgageTermEndDate, new Date())) {
				return fireError(`de hypotheek is al afgelost op ${convertDateObject(new Date(), true)}`, 'MortgageTermStartDate');
			}
		}

		// check if mortgage start in the future -> 30, 15-05-2058
		if (isAfter(parseDate(mortgageTermStart), new Date())) {
			setError(getFormName(formItems.step2.mortgagePart.mortgageTermStart))
			return fireError(`de hypotheek loopt nog niet op ${convertDateObject(new Date(), true)}`, 'MortgageTermStartDate');
		}

		clearErrors(getFormName(formItems.step2.mortgagePart.mortgageTermStart))
		return true;
	}

	/**
	 * Validate validateRvpEndDate
	 * @param interestRateFixedUntil
	 */
	function validateRvpEndDate(interestRateFixedUntil: string) {
		const mortgageTermStart: string = getValues(getFormName(formItems.step2.mortgagePart.mortgageTermStart));
		const mortgageTermYears = getValues(getFormName(formItems.step2.mortgagePart.mortgageTermYears));
		const interestRateFixedFor: number = parseInt(getValues(getFormName(formItems.step2.mortgagePart.interestRateFixedFor)));

		// check if is a valid date
		if (!isValidDate(interestRateFixedUntil)) {
			return fireError(`datum is niet correct`, 'RvpEndDate');
		}

		// check that rvp has not already ended -> 30, 16-05-2002 // 10, 27-09-2018
		if (isBefore(parseDate(interestRateFixedUntil), new Date())) {
			return fireError(`De rentevastperiode is al afgelopen`, 'RvpEndDate');
		}

		// check if the mortgage is already paid off -> 30, 16-05-2002 // 10, 27-09-2056
		if (mortgageTermStart && mortgageTermYears) {
			const mortgageTermEndDate = addYears(parseDate(mortgageTermStart), mortgageTermYears);
			if (isBefore(mortgageTermEndDate, parseDate(interestRateFixedUntil))) {
				return fireError(`De hypotheek is al afgelost op ${convertDateObject(mortgageTermEndDate, true)}`,'RvpEndDate');
			}
		}

		// check if the morgage has not started yet - > 30, 16-5-2022 // 10, 17-09-2028
		if (mortgageTermStart && interestRateFixedFor ) {
			const interestRateFixedFrom = subYears(parseDate(interestRateFixedUntil), interestRateFixedFor);
			if (!isAfter(addDays(interestRateFixedFrom,1), parseDate(mortgageTermStart))) {
				return fireError(`De hypotheek loopt nog niet op ${convertDateObject(interestRateFixedFrom, true)}`, 'RvpEndDate');
			}
		}

		if (interestRateFixedFor) {
			const maxDate = addYears(new Date(), interestRateFixedFor);
			if (isAfter(parseDate(interestRateFixedUntil), maxDate)) {
				return fireError(`De rentevastperiode ligt na ${convertDateObject(maxDate, true)}`, 'RvpEndDate');
			}
		}

		clearErrors(getFormName(formItems.step2.mortgagePart.interestRateFixedUntil))
		return true;
	}

	return (
		<Fragment>
			{form[formItems.step1.countMortgageParts] > 1 &&
        <Text
        as="h3">{`Hypotheekdeel ${part + 1}`}</Text>
			}
			<div className="mortgage-part__form">
			<InputHolder tooltip={formItems.step2.toolTips.currentInterestRate}>
				<InputHolderItem>
					<Select
						label="Hypotheekvorm*"
						name={getFormName(formItems.step2.mortgagePart.mortgageType)}
						id={getFormName(formItems.step2.mortgagePart.mortgageType)}
						register={register}
						value={getDefaultValue(values, formItems.step2.mortgagePart.mortgageType)}
						validation={{ required: true }}
						options={[
							{
								name: 'Annuïteitenhypotheek',
								value: 4,
							},
							{
								name: 'Aflossingsvrije hypotheek',
								value: 6,
							},
							{
								name: 'Lineaire hypotheek',
								value: 9,
							},
						]}
					/>
				</InputHolderItem>
				<InputHolderItem>
					<Input
						label="Huidige hypotheekrente*"
						name={getFormName(formItems.step2.mortgagePart.currentInterestRate)}
						id={getFormName(formItems.step2.mortgagePart.currentInterestRate)}
						type="percentage"
						hasError={hasError(formItems.step2.mortgagePart.currentInterestRate)}
						placeholder="2,7%"
						register={register}
						defaultValue={getDefaultValue(values, formItems.step2.mortgagePart.currentInterestRate)}
						validation={{ required: true, validate: {
								notAPercentage: (v: string) => isPercentage(v)
							}
						}}
					/>
					{hasError(formItems.step2.mortgagePart.currentInterestRate) && (
						<span className="form__error">Vul een geldige rente in</span>
					)}
				</InputHolderItem>
			</InputHolder>
			<InputHolder tooltip={formItems.step2.toolTips.mortgageTerm}>
				<InputHolderItem>
					<Input
						label="looptijd hypotheek (jaar)*"
						name={getFormName(formItems.step2.mortgagePart.mortgageTermYears)}
						id={getFormName(formItems.step2.mortgagePart.mortgageTermYears)}
						type="number"
						hasError={hasError(formItems.step2.mortgagePart.mortgageTermYears)}
						placeholder="10"
						register={register}
						defaultValue={30}
						validation={{ required: true, min: 1, max: 30, validate: {
								notWholeNumber: (v: number) => isWholeNumber(v)
							}
						}}
					/>
					{hasError(formItems.step2.mortgagePart.mortgageTermYears) && (
						<span className="form__error">Vul de looptijd in jaren</span>
					)}
				</InputHolderItem>
				<InputHolderItem>
						<Input
							label="Sinds*"
							type={'date'}
							name={getFormName(formItems.step2.mortgagePart.mortgageTermStart)}
							id={getFormName(formItems.step2.mortgagePart.mortgageTermStart)}
							hasError={hasError(formItems.step2.mortgagePart.mortgageTermStart)}
							placeholder="dd-mm-jjjj"
							register={register}
							defaultValue={getDefaultValue(values, formItems.step2.mortgagePart.mortgageTermStart)}
							validation={{ required: true, validate: {
									//validDate: (v: string) => isValidDate(v),
									correctStartDate: (v: string) => validateMortgageTermStartDate(v)
								}
							}}
						/>
					{hasError(formItems.step2.mortgagePart.mortgageTermStart) && (
						<span className="form__error">{mortgageTermStartError}</span>
					)}
				</InputHolderItem>
			</InputHolder>
			<InputHolder tooltip={formItems.step2.toolTips.interestRateFixed}>
				<InputHolderItem>
					<Input
						label="De rente staat vast voor (jaar)*"
						name={getFormName(formItems.step2.mortgagePart.interestRateFixedFor)}
						id={getFormName(formItems.step2.mortgagePart.interestRateFixedFor)}
						type="number"
						hasError={hasError(formItems.step2.mortgagePart.interestRateFixedFor)}
						placeholder="10"
						register={register}
						defaultValue={10}
						validation={{required: true, min: 1, max: 30, validate: {
								notWholeNumber: (v: number) => isWholeNumber(v)
							}
					}}
					/>
					{hasError(formItems.step2.mortgagePart.interestRateFixedFor) && (
						<span className="form__error">Vul de looptijd in jaren</span>
					)}
				</InputHolderItem>
				<InputHolderItem>
					<Input
						label="Tot*"
						type={'date'}
						name={getFormName(formItems.step2.mortgagePart.interestRateFixedUntil)}
						id={getFormName(formItems.step2.mortgagePart.interestRateFixedUntil)}
						hasError={hasError(formItems.step2.mortgagePart.interestRateFixedUntil)}
						placeholder="dd-mm-jjjj"
						register={register}
						defaultValue={getDefaultValue(values, formItems.step2.mortgagePart.interestRateFixedUntil)}
						validation={{ required: true, validate: {
								//validDate: (v: string) => isValidDate(v),
								correctEndDate: (v: string) => validateRvpEndDate(v)
							}
						}}
					/>
					{hasError(formItems.step2.mortgagePart.interestRateFixedUntil) && (
						<span className="form__error">{rvpEndDateError}</span>
					)}
				</InputHolderItem>
			</InputHolder>
			<InputHolder tooltip={formItems.step2.toolTips.mortgageAmountStartFixedInterestRate}>
				<InputHolderItem isFullWidth={true}>
					<Input
						label="Hoogte hypotheek(deel) bij start van de huidige rentevastperiode*"
						name={getFormName(formItems.step2.mortgagePart.mortgageAmountStartFixedInterestRate)}
						id={getFormName(formItems.step2.mortgagePart.mortgageAmountStartFixedInterestRate)}
						hasError={hasError(formItems.step2.mortgagePart.mortgageAmountStartFixedInterestRate)}
						type="currency"
						placeholder="€"
						register={register}
						defaultValue={getDefaultValue(values, formItems.step2.mortgagePart.mortgageAmountStartFixedInterestRate)}
						validation={{required: true, min: 1}}
					/>
					{hasError(formItems.step2.mortgagePart.mortgageAmountStartFixedInterestRate) && (
						<span className="form__error">Vul hoogte in van hypotheek(deel) bij start van de huidige rentevastperiode</span>
					)}
				</InputHolderItem>
			</InputHolder>
			<InputHolder isFlexWrap={true} tooltip={formItems.step2.toolTips.isThePenaltyAmountKnown}>
					<InputHolderItem isFlexBasis={true}>
						<label className="input__label" htmlFor="mortgage.0.mortgageAmountStartFixedInterestRate">
							Is de te betalen vergoeding (boeterente) bekend?*</label>
					</InputHolderItem>

					<InputHolderItem isSmall={true}>
						<Radio
							label="Ja"
							checked={(typeof getDefaultValue(values, formItems.step2.mortgagePart.isThePenaltyAmountKnown) === 'boolean')
								? getDefaultValue(values, formItems.step2.mortgagePart.isThePenaltyAmountKnown) === true : true}
							name={getFormName(formItems.step2.mortgagePart.isThePenaltyAmountKnown)}
							id={`${getFormName(formItems.step2.mortgagePart.isThePenaltyAmountKnown)}-1`}
							defaultValue={'true'}
							register={register}
							validation={{
								onChange: (e: any) =>
									setShowExtraFields(e.currentTarget.value === 'false'),
							}}
						/>
					</InputHolderItem>
					<InputHolderItem isSmall={true}>
						<Radio
							label="Nee"
							checked={(typeof getDefaultValue(values, formItems.step2.mortgagePart.isThePenaltyAmountKnown) === 'boolean')
								? getDefaultValue(values, formItems.step2.mortgagePart.isThePenaltyAmountKnown) === false : false}
							name={getFormName(formItems.step2.mortgagePart.isThePenaltyAmountKnown)}
							id={`${getFormName(formItems.step2.mortgagePart.isThePenaltyAmountKnown)}-2`}
							defaultValue={'false'}
							register={register}
						/>
					</InputHolderItem>

			</InputHolder>
			{!showExtraFields &&
				<InputHolder tooltip={formItems.step2.toolTips.penaltyAmount}>
					<InputHolderItem isFullWidth={true}>
						<Input
						label="Hoogte boeterente*"
						name={getFormName(formItems.step2.mortgagePart.penaltyAmount)}
						id={getFormName(formItems.step2.mortgagePart.penaltyAmount)}
						hasError={hasError(formItems.step2.mortgagePart.penaltyAmount)}
						type="currency"
						placeholder="€"
						register={register}
            defaultValue={getDefaultValue(values, formItems.step2.mortgagePart.penaltyAmount)}
            validation={{required: true}}
						/>
						{hasError(formItems.step2.mortgagePart.penaltyAmount) && (
							<span className="form__error">Vul hoogte in van boeterente</span>
						)}
					</InputHolderItem>
				</InputHolder>
			}
			{showExtraFields &&
				<InputHolder tooltip={formItems.step2.toolTips.comparativeInterest}>
					<InputHolderItem>
						<Input
						label="Percentage kostenloos extra aflossen*"
						name={getFormName(formItems.step2.mortgagePart.penaltyFreePayOffPercentage)}
						id={getFormName(formItems.step2.mortgagePart.penaltyFreePayOffPercentage)}
						hasError={hasError(formItems.step2.mortgagePart.penaltyFreePayOffPercentage)}
            type="percentage"
						placeholder="10%"
						register={register}
            defaultValue={getDefaultValue(values, formItems.step2.mortgagePart.penaltyFreePayOffPercentage)}
            validation={{ required: true, min: 1, validate: {
								notWholeNumber: (v: number) => isWholeNumber(v)
							}
						}}
						/>
						{hasError(formItems.step2.mortgagePart.penaltyFreePayOffPercentage) && (
							<span className="form__error">Vul hoogte in van percentage kostenloos aflossen</span>
						)}
					</InputHolderItem>
          <InputHolderItem>
            <Input
            label="Vergelijkingsrente*"
            name={getFormName(formItems.step2.mortgagePart.comparativeInterest)}
            id={getFormName(formItems.step2.mortgagePart.comparativeInterest)}
            hasError={hasError(formItems.step2.mortgagePart.comparativeInterest)}
            type="percentage"
            placeholder="1,5%"
            register={register}
            defaultValue={getDefaultValue(values, formItems.step2.mortgagePart.comparativeInterest)}
            validation={{required: true, validate: {
								notAPercentage: (v: string) => isPercentage(v)
							}
						}}
            />
						{hasError(formItems.step2.mortgagePart.comparativeInterest) && (
							<span className="form__error">Vul hoogte in van vergelijkingsrente</span>
						)}
          </InputHolderItem>
				</InputHolder>
			}
			</div>
		</Fragment>
	)
}
